import React, { useState, useEffect, useCallback } from 'react';
import {
  Table, Modal, Input, Button, message, Tabs, Tag, Spin, Tooltip
} from 'antd';
import {
  WalletOutlined, ThunderboltOutlined, ReloadOutlined,
  RiseOutlined, FallOutlined, GiftOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface WalletData {
  balance: number;
  total_recharged: number;
  total_consumed: number;
  updated_at: string | null;
}

interface RechargeOrder {
  order_no: string;
  source_type: string;
  points_amount: number;
  pay_amount: number;
  code_used: string | null;
  external_tx_id: string | null;
  status: string;
  pay_time: string | null;
  created_at: string;
}

interface PointLog {
  id: number;
  type: string;
  amount: number;
  related_id: string | null;
  model_id: string | null;
  model_name?: string | null;
  remark: string | null;
  created_at: string;
}

const sourceTypeMap: Record<string, string> = {
  payment: '在线支付',
  redeem_code: '兑换码',
  admin_gift: '管理员赠送',
  system_refund: '系统退款'
};

const statusColorMap: Record<string, string> = {
  success: 'green',
  pending: 'orange',
  failed: 'red',
  closed: 'default'
};

const statusLabelMap: Record<string, string> = {
  success: '成功',
  pending: '处理中',
  failed: '失败',
  closed: '已关闭'
};

const pointTypeColorMap: Record<string, string> = {
  recharge: 'green',
  consume: 'red',
  refund: 'blue',
  freeze: 'orange',
  unfreeze: 'cyan'
};

const pointTypeLabelMap: Record<string, string> = {
  recharge: '积分充值',
  consume: '积分消费',
  refund: '退还积分',
  freeze: '冻结积分',
  unfreeze: '解冻积分'
};

const ProfilePage: React.FC = () => {
  const { user } = useAuth();
  const [wallet, setWallet] = useState<WalletData>({ balance: 0, total_recharged: 0, total_consumed: 0, updated_at: null });
  const [walletLoading, setWalletLoading] = useState(false);

  const [orders, setOrders] = useState<RechargeOrder[]>([]);
  const [orderTotal, setOrderTotal] = useState(0);
  const [orderPage, setOrderPage] = useState(1);
  const [orderLoading, setOrderLoading] = useState(false);

  const [logs, setLogs] = useState<PointLog[]>([]);
  const [logTotal, setLogTotal] = useState(0);
  const [logPage, setLogPage] = useState(1);
  const [logLoading, setLogLoading] = useState(false);

  const [activeTab, setActiveTab] = useState('orders');

  // 充值 Modal
  const [rechargeModalVisible, setRechargeModalVisible] = useState(false);

  // 兑换 Modal
  const [redeemModalVisible, setRedeemModalVisible] = useState(false);
  const [redeemCode, setRedeemCode] = useState('');
  const [redeemLoading, setRedeemLoading] = useState(false);

  const loadWallet = useCallback(async () => {
    setWalletLoading(true);
    try {
      const res: any = await api.get('/profile/wallet');
      if (res.success) setWallet(res.data);
    } catch {
      message.error('加载钱包失败');
    } finally {
      setWalletLoading(false);
    }
  }, []);

  const loadOrders = useCallback(async (page = 1) => {
    setOrderLoading(true);
    try {
      const res: any = await api.get('/profile/recharge-orders', { params: { page, pageSize: 10 } });
      if (res.success) {
        setOrders(res.data.list);
        setOrderTotal(res.data.total);
      }
    } catch {
      message.error('加载充值记录失败');
    } finally {
      setOrderLoading(false);
    }
  }, []);

  const loadLogs = useCallback(async (page = 1) => {
    setLogLoading(true);
    try {
      const res: any = await api.get('/profile/point-logs', { params: { page, pageSize: 10 } });
      if (res.success) {
        setLogs(res.data.list);
        setLogTotal(res.data.total);
      }
    } catch {
      message.error('加载积分流水失败');
    } finally {
      setLogLoading(false);
    }
  }, []);

  useEffect(() => {
    loadWallet();
    loadOrders(1);
    loadLogs(1);
  }, [loadWallet, loadOrders, loadLogs]);

  const handleRefresh = () => {
    loadWallet();
    loadOrders(orderPage);
    loadLogs(logPage);
    message.success('数据已刷新');
  };

  const handleRedeem = async () => {
    if (!redeemCode.trim()) {
      message.warning('请输入兑换码');
      return;
    }
    setRedeemLoading(true);
    try {
      const res: any = await api.post('/profile/redeem', { code: redeemCode.trim() });
      if (res.success) {
        message.success(res.message || '兑换成功');
        setRedeemModalVisible(false);
        setRedeemCode('');
        loadWallet();
        loadOrders(1);
        loadLogs(1);
        setOrderPage(1);
        setLogPage(1);
      } else {
        message.error(res.message || '兑换失败');
      }
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || '兑换失败';
      message.error(msg);
    } finally {
      setRedeemLoading(false);
    }
  };

  const orderColumns: ColumnsType<RechargeOrder> = [
    {
      title: '订单号',
      dataIndex: 'order_no',
      width: 200,
      ellipsis: { showTitle: false },
      render: (v: string) => (
        <Tooltip title={v}>
          <span>{v}</span>
        </Tooltip>
      )
    },
    {
      title: '支付类型',
      dataIndex: 'source_type',
      width: 120,
      render: (v: string) => sourceTypeMap[v] || v
    },
    {
      title: '兑换码',
      dataIndex: 'code_used',
      width: 180,
      ellipsis: { showTitle: false },
      render: (v: string | null) => v ? (
        <Tooltip title={v}>
          <code className="text-xs">{v}</code>
        </Tooltip>
      ) : '-'
    },
    {
      title: '获得积分',
      dataIndex: 'points_amount',
      width: 120,
      render: (v: number) => <span className="text-green-600 font-semibold">+{v}</span>
    },
    {
      title: '充值金额',
      dataIndex: 'pay_amount',
      width: 110,
      render: (v: number) => v > 0 ? `¥${Number(v).toFixed(2)}` : '-'
    },
    {
      title: '到账时间',
      dataIndex: 'pay_time',
      width: 160,
      render: (v: string | null) => v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '-'
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 90,
      render: (v: string) => <Tag color={statusColorMap[v] || 'default'}>{statusLabelMap[v] || v}</Tag>
    }
  ];

  const logColumns: ColumnsType<PointLog> = [
    {
      title: '类型',
      dataIndex: 'type',
      width: 100,
      render: (v: string) => <Tag color={pointTypeColorMap[v] || 'default'}>{pointTypeLabelMap[v] || v}</Tag>
    },
    {
      title: '积分变动',
      dataIndex: 'amount',
      width: 110,
      render: (v: number) => (
        <span className={v >= 0 ? 'text-green-600 font-semibold' : 'text-red-500 font-semibold'}>
          {v >= 0 ? `+${v}` : v}
        </span>
      )
    },
    {
      title: '模型类型',
      dataIndex: 'model_name',
      width: 150,
      ellipsis: { showTitle: false },
      render: (v: string | null) => v ? (
        <Tooltip title={v}>
          <span>{v}</span>
        </Tooltip>
      ) : '-'
    },
    {
      title: '描述',
      dataIndex: 'remark',
      width: 180,
      ellipsis: { showTitle: false },
      render: (v: string | null) => v ? (
        <Tooltip title={v}>
          <span>{v}</span>
        </Tooltip>
      ) : '-'
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      width: 160,
      render: (v: string) => dayjs(v).format('YYYY-MM-DD HH:mm')
    }
  ];

  const avatarLetter = (user?.user_name || 'U').charAt(0).toUpperCase();
  const userType = user?.user_type === 'pro' ? 'VIP会员' : '试用用户';
  const userTypeColor = user?.user_type === 'pro' ? 'from-indigo-500 to-purple-500' : 'from-gray-400 to-gray-500';

  return (
    <div className="min-h-screen bg-gray-50 px-12 py-6">
      {/* 顶部标题栏 */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">个人中心</h1>
          <p className="text-gray-500 text-sm mt-1">管理您的账户信息与积分资产</p>
        </div>
        <button
          onClick={handleRefresh}
          className="flex items-center space-x-2 px-4 py-2 rounded-xl border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 transition-all text-sm"
        >
          <ReloadOutlined />
          <span>刷新数据</span>
        </button>
      </div>

      {/* 顶部统计卡片 */}
      <Spin spinning={walletLoading}>
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 border-l-4 border-l-indigo-500">
            <div className="flex items-center space-x-2 text-gray-500 text-sm mb-3">
              <WalletOutlined className="text-indigo-500" />
              <span>当前可用积分</span>
            </div>
            <div className="text-3xl font-bold text-indigo-600">
              {wallet.balance.toLocaleString()}
            </div>
          </div>
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 border-l-4 border-l-orange-400">
            <div className="flex items-center space-x-2 text-gray-500 text-sm mb-3">
              <FallOutlined className="text-orange-500" />
              <span>累计消耗积分</span>
            </div>
            <div className="text-3xl font-bold text-gray-800">
              {wallet.total_consumed.toLocaleString()}
            </div>
          </div>
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 border-l-4 border-l-green-500">
            <div className="flex items-center space-x-2 text-gray-500 text-sm mb-3">
              <RiseOutlined className="text-green-500" />
              <span>累计充值积分</span>
            </div>
            <div className="text-3xl font-bold text-gray-800">
              {wallet.total_recharged.toLocaleString()}
            </div>
          </div>
        </div>
      </Spin>

      {/* 中部：用户信息 + 余额操作 */}
      <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6 mb-6">
        {/* 左：用户信息卡片 */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 flex flex-col items-center">
          <div className={`w-20 h-20 bg-gradient-to-br ${userTypeColor} rounded-full flex items-center justify-center mb-3`}>
            <span className="text-white text-3xl font-bold">{avatarLetter}</span>
          </div>
          <div className="text-lg font-bold text-gray-900">{user?.user_name || '-'}</div>
          <div className="text-gray-400 text-xs mt-1">ID: {user?.user_id || '-'}</div>
          <div className={`mt-2 px-3 py-1 rounded-full text-xs font-medium text-white bg-gradient-to-r ${userTypeColor}`}>
            {userType}
          </div>
          <div className="mt-4 w-full border-t border-gray-200 pt-4 space-y-2">
            <div className="flex justify-between text-xs text-gray-500">
              <span>注册时间</span>
              <span>{user?.created_at ? dayjs(user.created_at).format('YYYY.MM.DD') : '-'}</span>
            </div>
            <div className="flex justify-between text-xs text-gray-500">
              <span>账户类型</span>
              <span>{userType}</span>
            </div>
          </div>
        </div>

        {/* 右：余额 + 快捷操作卡片 */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 flex flex-col justify-center">
          <div className="mb-6">
            <div className="text-gray-500 text-sm mb-2">账户余额</div>
            <div className="flex items-baseline space-x-2">
              <span className="text-4xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                {wallet.balance.toLocaleString()}
              </span>
              <span className="text-gray-400 text-sm">积分</span>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-5">
            <div className="text-gray-500 text-sm mb-3">快捷操作</div>
            <div className="flex gap-3">
              <button
                onClick={() => setRechargeModalVisible(true)}
                className="flex-1 flex items-center justify-center space-x-2 py-3 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-medium hover:opacity-90 transition-all"
              >
                <ThunderboltOutlined />
                <span>+ 积分充值</span>
              </button>
              <button
                onClick={() => { setRedeemCode(''); setRedeemModalVisible(true); }}
                className="flex-1 flex items-center justify-center space-x-2 py-3 rounded-xl border-2 border-indigo-200 text-indigo-600 font-medium hover:bg-indigo-50 transition-all"
              >
                <GiftOutlined />
                <span>兑换积分</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 底部详细记录 */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
        <div className="text-base font-semibold text-gray-900 mb-4">详细记录</div>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: 'orders',
              label: '充值记录',
              children: (
                <Table
                  rowKey="order_no"
                  columns={orderColumns}
                  dataSource={orders}
                  loading={orderLoading}
                  pagination={{
                    current: orderPage,
                    pageSize: 10,
                    total: orderTotal,
                    onChange: (p) => { setOrderPage(p); loadOrders(p); },
                    showTotal: (t) => `共 ${t} 条`
                  }}
                  scroll={{ x: 1020 }}
                />
              )
            },
            {
              key: 'logs',
              label: '积分流水',
              children: (
                <Table
                  rowKey="id"
                  columns={logColumns}
                  dataSource={logs}
                  loading={logLoading}
                  pagination={{
                    current: logPage,
                    pageSize: 10,
                    total: logTotal,
                    onChange: (p) => { setLogPage(p); loadLogs(p); },
                    showTotal: (t) => `共 ${t} 条`
                  }}
                  scroll={{ x: 700 }}
                />
              )
            }
          ]}
        />
      </div>

      {/* 充值 Modal（开发中） */}
      <Modal
        title="积分充值"
        open={rechargeModalVisible}
        onCancel={() => setRechargeModalVisible(false)}
        footer={null}
        centered
      >
        <div className="py-8 text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <ThunderboltOutlined className="text-2xl text-indigo-600" />
          </div>
          <div className="text-lg font-semibold text-gray-800 mb-2">功能开发中</div>
          <div className="text-gray-500 text-sm">充值功能即将上线，敬请期待</div>
          <div className="mt-4 px-4 py-3 bg-indigo-50 rounded-xl text-indigo-700 text-sm">
            充值比例：<strong>1元 = 100积分</strong>
          </div>
        </div>
      </Modal>

      {/* 兑换 Modal */}
      <Modal
        title="兑换积分"
        open={redeemModalVisible}
        onCancel={() => { setRedeemModalVisible(false); setRedeemCode(''); }}
        onOk={handleRedeem}
        okText="立即兑换"
        cancelText="取消"
        confirmLoading={redeemLoading}
        centered
      >
        <div className="py-4">
          <div className="mb-4">
            <div className="text-sm text-gray-600 mb-2">请输入兑换码</div>
            <Input
              size="large"
              placeholder="请输入32位兑换码"
              value={redeemCode}
              onChange={(e) => setRedeemCode(e.target.value)}
              onPressEnter={handleRedeem}
              prefix={<GiftOutlined className="text-indigo-400" />}
            />
          </div>
          <div className="bg-gray-50 rounded-xl p-4 space-y-1 text-xs text-gray-500">
            <div>• 每个兑换码仅可使用一次</div>
            <div>• 兑换成功后积分立即到账</div>
            <div>• 如有问题请联系客服</div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ProfilePage;
